// Background: the customer cycles through every design of the same product type (Ryan, 2 Nov 2026: "each product has at least 30,
// they should be able to cycle to all of it"). A design is another product of the same shape (catalog.js, backgroundChoices).
//
// Picking one changes the product photo and print line of every side; the customer's photos and text move with the print line
// (editor.setBackground). The order then goes to that design's own product: ctx.handle, and the variant of the same size (ctx.variant),
// so the cart gets the design the customer chose. All designs of a product type cost the same (Ryan, 2 Nov).
//
// The shop is asked for the new design's sizes first. If it cannot answer, the background does not change (the cart would not know the
// variant), except in preview mode, where nothing goes into a cart.

import { $, esc, shortTitle } from "./util.js";
import { backgroundChoices, loadBackground } from "./catalog.js";
import { loadStoreOffer } from "./store-sizes.js";
import { matchSize } from "./bulk.js";

const THUMB_W = 240;

// A smaller copy of a Shopify photo (cdn.shopify.com takes a width).
function thumbUrl(url) {
  try { const u = new URL(url); u.searchParams.set("width", String(THUMB_W)); return u.href; } catch (e) { return url; }
}

// The variant of the new design that has the same size as the one chosen. before: the size info of the design being left (or null).
export function sameSizeVariant(before, variant, after) {
  if (!after) return "";
  const had = before && Array.isArray(before.sizes) ? before.sizes.find((s) => s.variantId && s.variantId === String(variant || "")) : null;
  const label = had ? had.label : (before && before.defaultLabel) || "";
  let s = label ? matchSize(label, after.sizes) : null;
  if (!s || !s.variantId) s = after.sizes.find((x) => x.variantId && !x.soldOut) || after.sizes.find((x) => x.variantId) || null;
  return (s && s.variantId) || after.defaultVariantId || "";
}

// editor: the main editor. ctx: { shop, product, handle, variant, returnUrl, embed } (main.js; changed here when a design is picked).
// liveSizes: a promise of the size info of the product the page opened with. onChange(product): the page shows the new name.
export function initBackgrounds({ editor, ctx, liveSizes, onChange }) {
  const box = $("bgPicker"), strip = $("bgStrip"), count = $("bgCount"), nameEl = $("bgName"), prev = $("bgPrev"), next = $("bgNext");
  const previewMode = !ctx.returnUrl && !ctx.embed;
  let list = [], switching = false;
  let info = liveSizes ? liveSizes.catch(() => null) : Promise.resolve(null);      // the size info of the design on the page

  const at = () => list.findIndex((c) => c.handle === editor.product.handle);
  function render() {
    const i = at();
    strip.innerHTML = list.map((c, k) => `<li><button class="bg-thumb" type="button" data-k="${k}" aria-pressed="${k === i}" aria-label="${esc(shortTitle(c.title))}" ${switching ? "disabled" : ""}>
      <img src="${esc(thumbUrl(c.thumb))}" alt="" loading="lazy" decoding="async" width="${THUMB_W}" height="${THUMB_W}"></button></li>`).join("");
    count.textContent = i >= 0 ? `${i + 1} of ${list.length}` : `${list.length} designs`;
    nameEl.textContent = shortTitle(editor.product.title);
    prev.disabled = next.disabled = switching;
    box.setAttribute("aria-busy", String(switching));
  }
  // Brings the chosen design into view in the middle of the row (the row scrolls on its own; the page never scrolls sideways).
  function reveal() {
    const on = strip.querySelector('[aria-pressed="true"]');
    if (!on) return;
    const li = on.parentElement;
    strip.scrollTo({ left: Math.max(0, li.offsetLeft - (strip.clientWidth - li.offsetWidth) / 2), behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }

  async function pick(k) {
    const c = list[k];
    if (!c || switching || c.handle === editor.product.handle) return;
    switching = true; render();
    try {
      let offer = null;
      if (ctx.shop) offer = await loadStoreOffer({ shop: ctx.shop, handle: c.handle });
      if (!offer && !previewMode) { editor.toast("This design could not be loaded. Check your internet, then try again."); return; }
      const loaded = await loadBackground(editor.product, c.handle);
      const before = await info;
      if (!(await editor.setBackground(loaded.product, loaded.sides))) return;
      ctx.handle = c.handle; ctx.product = loaded.product.id;
      ctx.variant = offer ? sameSizeVariant(before, ctx.variant, offer.info) : "";
      if (offer) { info = Promise.resolve(offer.info); editor.bulk.setInfo(offer.info); }
      onChange(loaded.product);
    } catch (e) {
      console.error("RightGifting: the background could not be changed", e);
      editor.toast("This design could not be loaded. Please try again.");
    } finally { switching = false; render(); reveal(); }
  }

  strip.addEventListener("click", (e) => { const b = e.target.closest("[data-k]"); if (b) pick(Number(b.dataset.k)); });
  // The arrows go round: after the last design comes the first again.
  const step = (d) => { if (list.length > 1) pick(((at() < 0 ? 0 : at()) + d + list.length) % list.length); };
  prev.addEventListener("click", () => step(-1));
  next.addEventListener("click", () => step(1));

  // Shown only when there are at least two designs to choose from.
  (async () => {
    try { list = await backgroundChoices(editor.product, editor.sides().map((s) => s.id)); } catch (e) { list = []; }
    if (list.length < 2) return;
    box.hidden = false;
    render(); reveal();
  })();

  return { refresh: render };
}
